import React, { useState } from 'react';
import { VideoUploader } from '../components/VideoUploader';
import { Results } from '../components/Results';
import { useAuth } from '../context/AuthContext';
import { Wand2, AlertTriangle, Loader2, Settings2, Hash, FileText, BrainCircuit } from 'lucide-react';
import { uploadToS3 } from '../utils/s3';
import { transcribeVideo } from '../utils/aws';
import { generateCaption, generateContentGaps, generateTitle } from '../utils/openai';
import { validateConfig } from '../utils/config';
import { supabase } from '../utils/supabase';
import toast from 'react-hot-toast';
import type { CaptionConfig, ProcessingResult } from '../types';

interface ProcessingError {
  step: 'upload' | 'transcribe' | 'caption' | 'gaps';
  message: string;
}

export function Tool() {
  const { user } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState<string>('');
  const [processingError, setProcessingError] = useState<ProcessingError | null>(null);
  const [processingResult, setProcessingResult] = useState<ProcessingResult | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const [config, setConfig] = useState<CaptionConfig>({
    tone: 'professional',
    length: 'medium',
    includeHashtags: false,
    includeContentGaps: false,
    customPrompt: '',
    customInstructions: ''
  });

  const toneOptions = [
    { value: 'professional', label: 'Professional' },
    { value: 'casual', label: 'Casual' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'humorous', label: 'Humorous' },
    { value: 'thought-leader', label: 'Thought Leader' },
    { value: 'industry-insider', label: 'Industry Insider' },
    { value: 'inspirational', label: 'Inspirational' }
  ];

  const handleVideoSelect = (file: File, duration: number) => {
    setSelectedFile(file);
    setVideoDuration(duration);
    setProcessingResult(null);
    setProcessingError(null);
  };

  const handleConfigChange = (field: keyof CaptionConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const processVideo = async () => {
    if (!selectedFile || !videoDuration) {
      toast.error('Please select a video first');
      return;
    }

    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    let jobId: string | null = null;

    try {
      setIsProcessing(true);
      setProcessingError(null);
      setConfigError(null);

      // Validate environment configuration
      try {
        await validateConfig();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Invalid environment configuration';
        setConfigError(errorMessage);
        throw new Error('Please configure the environment variables in the admin panel before processing videos.');
      }

      // Create initial job record
      const { data: job, error: jobError } = await supabase
        .from('transcription_jobs')
        .insert({
          user_id: user.id,
          title: selectedFile.name,
          transcript: '',
          caption: '',
          duration_seconds: videoDuration,
          processing_started_at: new Date().toISOString(),
          video_metadata: {
            filename: selectedFile.name,
            size: selectedFile.size,
            type: selectedFile.type,
            duration: videoDuration
          }
        })
        .select()
        .single();

      if (jobError) throw jobError;
      jobId = job.id;

      // Upload video
      setProcessingStep('Uploading video...');
      let s3Url;
      try {
        s3Url = await uploadToS3(selectedFile);
        toast.success('Video uploaded successfully');
      } catch (error) {
        setProcessingError({
          step: 'upload',
          message: error instanceof Error ? error.message : 'Failed to upload video'
        });
        throw error;
      }

      // Transcribe video
      setProcessingStep('Transcribing video...');
      let transcriptionResult;
      try {
        transcriptionResult = await transcribeVideo(s3Url);
        toast.success('Video transcribed successfully');
      } catch (error) {
        setProcessingError({
          step: 'transcribe',
          message: error instanceof Error ? error.message : 'Failed to transcribe video'
        });
        throw error;
      }

      // Generate caption
      setProcessingStep('Generating caption...');
      let caption;
      try {
        caption = await generateCaption(transcriptionResult.text, config);
        toast.success('Caption generated successfully');
      } catch (error) {
        setProcessingError({
          step: 'caption',
          message: error instanceof Error ? error.message : 'Failed to generate caption'
        });
        throw error;
      }

      // Generate content gaps if requested
      let contentGaps = [];
      if (config.includeContentGaps) {
        setProcessingStep('Analyzing content gaps...');
        try {
          contentGaps = await generateContentGaps(transcriptionResult.text);
          toast.success('Content gaps analyzed successfully');
        } catch (error) {
          setProcessingError({
            step: 'gaps',
            message: error instanceof Error ? error.message : 'Failed to analyze content gaps'
          });
          contentGaps = [];
        }
      }

      // Generate title
      setProcessingStep('Generating title...');
      let title;
      try {
        title = await generateTitle(transcriptionResult.text);
      } catch (error) {
        console.error('Error generating title:', error);
        title = 'Untitled Transcription';
      }

      // Update transcription job with results
      const { error: updateError } = await supabase
        .from('transcription_jobs')
        .update({
          title,
          transcript: transcriptionResult.text,
          caption,
          content_gaps: contentGaps,
          processing_completed_at: new Date().toISOString()
        })
        .eq('id', jobId);

      if (updateError) throw updateError;

      setProcessingResult({
        transcript: transcriptionResult.text,
        caption,
        contentGaps
      });
      
      toast.success('Video processed successfully!');
    } catch (error) {
      console.error('Error processing video:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to process video');

      // Update job with error status if we have a job ID
      if (jobId) {
        await supabase
          .from('transcription_jobs')
          .update({
            processing_completed_at: new Date().toISOString()
          })
          .eq('id', jobId);
      }
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {configError && (
          <div className="mb-8 p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/20">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-500 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-500">
                  Configuration Required
                </h3>
                <div className="mt-2 text-sm text-yellow-500/90">
                  <p>{configError}</p>
                  {configError.includes('environment variables') && (
                    <p className="mt-2">
                      Please visit the admin panel to configure the required environment variables.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Upload and Settings */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-card rounded-2xl shadow-sm p-6 border">
              <h2 className="text-xl font-semibold text-foreground mb-6">Create Post</h2>
              
              <div className="space-y-8">
                {/* Video Upload */}
                <VideoUploader
                  onVideoSelect={handleVideoSelect}
                  selectedFile={selectedFile}
                />

                {/* Settings Accordion */}
                <div className="space-y-4">
                  {/* Tone & Length */}
                  <div className="border border-border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Settings2 className="w-5 h-5 text-primary" />
                      <h3 className="font-medium text-foreground">Style Settings</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Tone
                        </label>
                        <select
                          value={config.tone}
                          onChange={(e) => handleConfigChange('tone', e.target.value)}
                          className="w-full rounded-lg border-input bg-background text-foreground focus:border-primary focus:ring-primary"
                        >
                          {toneOptions.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Length
                        </label>
                        <select
                          value={config.length}
                          onChange={(e) => handleConfigChange('length', e.target.value)}
                          className="w-full rounded-lg border-input bg-background text-foreground focus:border-primary focus:ring-primary"
                        >
                          <option value="short">Short</option>
                          <option value="medium">Medium</option>
                          <option value="long">Long</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Additional Features */}
                  <div className="border border-border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <BrainCircuit className="w-5 h-5 text-primary" />
                      <h3 className="font-medium text-foreground">Additional Features</h3>
                    </div>
                    <div className="space-y-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.includeHashtags}
                          onChange={(e) => handleConfigChange('includeHashtags', e.target.checked)}
                          className="rounded border-input bg-background text-primary focus:ring-primary"
                        />
                        <div className="flex items-center gap-2">
                          <Hash className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-foreground">Include hashtags</span>
                        </div>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={config.includeContentGaps}
                          onChange={(e) => handleConfigChange('includeContentGaps', e.target.checked)}
                          className="rounded border-input bg-background text-primary focus:ring-primary"
                        />
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm text-foreground">Content gap analysis</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Custom Instructions */}
                  <div className="border border-border rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Settings2 className="w-5 h-5 text-primary" />
                      <h3 className="font-medium text-foreground">Custom Instructions</h3>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Custom Prompt
                        </label>
                        <textarea
                          value={config.customPrompt}
                          onChange={(e) => handleConfigChange('customPrompt', e.target.value)}
                          placeholder="Enter your custom prompt..."
                          className="w-full rounded-lg border-input bg-background text-foreground focus:border-primary focus:ring-primary resize-none"
                          rows={3}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Additional Instructions
                        </label>
                        <textarea
                          value={config.customInstructions}
                          onChange={(e) => handleConfigChange('customInstructions', e.target.value)}
                          placeholder="Add any specific instructions..."
                          className="w-full rounded-lg border-input bg-background text-foreground focus:border-primary focus:ring-primary resize-none"
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <button
                  onClick={processVideo}
                  disabled={isProcessing}
                  className="w-full flex items-center justify-center px-6 py-3 rounded-xl text-primary-foreground font-medium transition-all bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 shadow-sm"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {processingStep}
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5 mr-2" />
                      Generate Caption
                    </>
                  )}
                </button>

                {processingError && (
                  <div className="p-4 bg-destructive/10 rounded-xl">
                    <div className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                      <div className="ml-2">
                        <h4 className="text-sm font-medium text-destructive">
                          Error during {processingError.step}
                        </h4>
                        <p className="mt-1 text-sm text-destructive/90">{processingError.message}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="lg:col-span-2">
            {processingResult ? (
              <Results
                transcript={processingResult.transcript}
                caption={processingResult.caption}
                contentGaps={processingResult.contentGaps}
              />
            ) : (
              <div className="bg-card rounded-2xl shadow-sm p-8 border h-full flex items-center justify-center">
                <div className="text-center">
                  <Wand2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    Ready to Generate
                  </h3>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Upload a video and configure your settings to generate a professional caption for your post.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}